import { isPlatformBrowser } from '@angular/common'
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Output,
  PLATFORM_ID,
} from '@angular/core'
import { ViewportService } from '@services/core/viewport.service'
import { LayoutService } from '@services/layout/layout.service'
import { ThemeService } from '@services/layout/theme.service'
import { MenuService } from '@services/shared/menu.service'
import { SharedModule } from '@shared/modules/shared.module'
import { MenuItem } from 'primeng/api'
import { ItemMenuComponent } from '../item-menu/item-menu.component'

@Component({
  selector: 'app-sidebar',
  imports: [SharedModule, ItemMenuComponent],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class AppSidebarComponent {
  modelUpper: any[] = []
  modelLower: any[] = []

  isSidebarOpen = true
  wasMobile = false
  currentTheme: 'light' | 'dark' = 'light'

  @Output() sidebarToggle = new EventEmitter<boolean>()

  toggleSidebar() {
    this.isSidebarOpen = !this.isSidebarOpen
    this.sidebarToggle.emit(this.isSidebarOpen)
  }

  screenWidth: number = 1920

  constructor(
    public layoutService: LayoutService,
    public el: ElementRef,
    private themeService: ThemeService,
    private viewportService: ViewportService,
    private menuService: MenuService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.screenWidth = window.innerWidth
    }
  }

  @HostListener('window:resize', [])
  onResize(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return
    }

    this.screenWidth = window.innerWidth

    if (this.screenWidth >= 1500) {
      this.isSidebarOpen = true
    } else if (this.screenWidth <= 844) {
      this.isSidebarOpen = true
    } else {
      this.isSidebarOpen = false
    }
    const isMobile = this.viewportService.isMobileView()

    if (isMobile && !this.wasMobile) {
      this.menuService.openMenu('sidebar')
    }

    this.wasMobile = isMobile
  }

  isMobile(): boolean {
    return this.viewportService.isMobileView()
  }

  handleMenuItemClick(item: MenuItem): void {
    if (this.isMobile() && (!item.items || item.items.length === 0)) {
      this.menuService.closeMenu('sidebar')
      this.isSidebarOpen = true
      this.sidebarToggle.emit(false)
    }
  }

  userRole: string | null = null
  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.userRole = localStorage.getItem('USER_ROLE')
    }

    this.menuService.menuState$.subscribe(() => {
      this.isSidebarOpen = true
    })

    this.modelUpper = [
      {
        logo: 'assets/layout/images/logo-complete.png',
      },
      {
        label: 'Inicio',
        id: 'home',
        icon: 'MessageSquareText',
        routerLink: ['home'],
      },
      {
        label: 'Hotel',
        id: 'hotel',
        icon: 'MessageSquareText',
        routerLink: ['hotel'],
      },
      {
        label: 'Apartamentos',
        id: 'apartments',
        icon: 'MessageSquareText',
        routerLink: ['apartments'],
      },
      {
        label: 'Servicios',
        id: 'services',
        icon: 'MessageSquareText',
        routerLink: ['services'],
      },
      {
        label: 'Eventos',
        id: 'events',
        icon: 'MessageSquareText',
        routerLink: ['events'],
      },
      {
        label: 'Grupos',
        id: 'groups',
        icon: 'MessageSquareText',
        routerLink: ['groups'],
      },
      {
        label: 'Media',
        id: 'media',
        icon: 'MessageSquareText',
        routerLink: ['media'],
      },
    ]

    this.modelUpper.push({ separator: true })

    const legalPagesItem: any = {
      label: 'Páginas legales',
      id: 'legal-pages',
      icon: 'FileText',
      items: [
        {
          label: 'Políticas de cookies',
          id: 'cookies-policy',
          icon: 'Cookie',
          routerLink: ['legal', 'cookies-policy'],
        },
        {
          label: 'Información a los trabajadores',
          id: 'worker-information',
          icon: 'Users',
          routerLink: ['legal', 'worker-information'],
        },
        {
          label: 'Aviso legal',
          id: 'legal-notice',
          icon: 'Scale',
          routerLink: ['legal', 'legal-notice'],
        },
        {
          label: 'Condiciones de reserva',
          id: 'reservation-conditions',
          icon: 'Scale',
          routerLink: ['legal', 'reservation-conditions'],
        },
        {
          label: 'Política de privacidad',
          id: 'privacy-policy',
          icon: 'Shield',
          routerLink: ['legal', 'privacy-policy'],
        },
      ],
    }

    this.modelUpper.push(legalPagesItem)

    const configItem: any = {
      label: 'Configuración',
      id: 'configuration',
      icon: 'Settings',
      items: [],
    }

    if (this.userRole === 'admin') {
      configItem.items = [
        {
          label: 'General',
          id: 'general-configuration',
          icon: 'Settings2',
          routerLink: ['settings', 'general-configuration'],
        },
        {
          label: 'Usuarios',
          id: 'users',
          icon: 'User',
          routerLink: ['users'],
        },
        {
          label: 'Clientes',
          id: 'clients',
          icon: 'User',
          routerLink: ['clients'],
        },
        {
          label: 'Roles',
          id: 'roles',
          icon: 'User',
          routerLink: ['roles'],
        },
        {
          label: 'Emails',
          id: 'emails',
          icon: 'MessageSquareText',
          routerLink: ['settings', 'emails'],
        },
        {
          label: 'Contacto',
          id: 'contact',
          icon: 'MessageSquareText',
          routerLink: ['contact'],
        },
      ]

      this.modelUpper.push(configItem)
    }

    this.modelLower = [
      {
        type: 'button',
        id: 'isSidebarOpen',
        label: 'Cerrar panel',
        icon: 'PanelLeftClose',
        action: () => this.toggleSidebar(),
      },
    ]

    this.themeService.theme$.subscribe((theme) => {
      this.currentTheme = theme as 'light' | 'dark'
    })

    this.themeService.setTheme(this.currentTheme)
  }

  switchTheme(theme?: 'light' | 'dark') {
    const newTheme = theme || (this.currentTheme === 'light' ? 'dark' : 'light')
    this.themeService.setTheme(newTheme)
  }

  isLightTheme(): boolean {
    return this.currentTheme === 'light'
  }

  isDarkTheme(): boolean {
    return this.currentTheme === 'dark'
  }
}
